import React, { useState } from 'react';
import { useLessonBuilderStore, LessonBlock } from '../../stores/lessonBuilderStore';
import { RichTextEditor } from './RichTextEditor';
import { Plus, Trash2, Check } from 'lucide-react';

const LESSON_API =
  import.meta.env.PROD
    ? 'https://api.clarityofconcept.com/api'
    : (import.meta.env.VITE_LESSON_API_URL || 'http://localhost:8000/api');

export const BlockEditor: React.FC = () => {
  const pages = useLessonBuilderStore((s) => s.pages);
  const selectedPageId = useLessonBuilderStore((s) => s.selectedPageId);
  const selectedBlockId = useLessonBuilderStore((s) => s.selectedBlockId);
  const updatePayload = useLessonBuilderStore((s) => s.updatePayload);
  const updateBlock = useLessonBuilderStore((s) => s.updateBlock);
  const deleteBlock = useLessonBuilderStore((s) => s.deleteBlock);
  const [riveUploading, setRiveUploading] = useState(false);

  const page = pages.find((p) => p.id === selectedPageId);
  const block = page?.blocks.find((b) => b.id === selectedBlockId);

  if (!page || !block) {
    return (
      <div className="w-80 border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-2xl shadow-sm p-4 text-sm text-gray-500 dark:text-gray-400">
        Select a block to edit it.
      </div>
    );
  }

  const update = (patch: Record<string, any>) => updatePayload(page.id, block.id, patch);

  const renderHtml = (payload: any, extra: Record<string, any> = {}) => {
    const next = { ...payload, ...extra };
    const align = next.align || 'left';
    const showHeading = next.showHeading !== false;
    if (next.useHtml && typeof next.html === 'string' && next.html.trim()) {
      return next.html;
    }
    const heading = showHeading ? (next.heading || '').trim() : '';
    const body = (next.body || '').trim();
    const htmlParts: string[] = [];
    if (heading) htmlParts.push(`<h3>${heading}</h3>`);
    if (body) htmlParts.push(`<p>${body}</p>`);
    const html = htmlParts.join('') || '<p></p>';
    return `<div style="text-align:${align};">${html}</div>`;
  };

  const renderEditor = (b: LessonBlock) => {
    switch (b.type) {
      case 'text': {
        const showHeading = b.payload.showHeading !== false;
        const useHtml = Boolean(b.payload.useHtml);
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              {!useHtml && (
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={showHeading}
                    onChange={(e) =>
                      update({
                        showHeading: e.target.checked,
                        html: renderHtml(b.payload, { showHeading: e.target.checked, useHtml: false }),
                      })
                    }
                  />
                  Show heading
                </label>
              )}
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={useHtml}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    const fallbackHtml = renderHtml({ ...b.payload, useHtml: false });
                    update({
                      useHtml: enabled,
                      html: enabled ? (b.payload.html || fallbackHtml) : fallbackHtml,
                    });
                  }}
                />
                Rich text mode
              </label>
            </div>

            {showHeading && !useHtml && (
              <>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Heading</label>
                <input
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
                  value={b.payload.heading || ''}
                  onChange={(e) =>
                    update({
                      heading: e.target.value,
                      html: renderHtml(b.payload, { heading: e.target.value }),
                    })
                  }
                  placeholder="Heading"
                />
              </>
            )}

            {!useHtml && (
              <>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Body</label>
                <textarea
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
                  rows={4}
                  value={b.payload.body || ''}
                  onChange={(e) =>
                    update({
                      body: e.target.value,
                      html: renderHtml(b.payload, { body: e.target.value }),
                    })
                  }
                  placeholder="Add your text..."
                />
              </>
            )}

            {useHtml && (
              <>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">
                  Rich text content
                </label>
                <RichTextEditor
                  value={b.payload.html || ''}
                  onChange={(html) => update({ html, useHtml: true })}
                  placeholder="Type, format, add links…"
                  align={(b.payload.align as any) || 'left'}
                  onAlignChange={(a) => update({ align: a })}
                />
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Allowed: headings, bold/italic/underline, lists, links, quotes, code.
                </div>
              </>
            )}

            {!useHtml && (
              <>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Align</label>
                <select
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
                  value={b.payload.align || 'left'}
                  onChange={(e) =>
                    update({
                      align: e.target.value,
                      html: renderHtml(b.payload, { align: e.target.value, useHtml: false }),
                    })
                  }
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </>
            )}
          </div>
        );
      }
      case 'image':
        return (
          <div className="space-y-3">
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Image URL</label>
            <input
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
              value={b.payload.url || ''}
              onChange={(e) => update({ url: e.target.value })}
              placeholder="https://..."
            />
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Caption</label>
            <input
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
              value={b.payload.caption || ''}
              onChange={(e) => update({ caption: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Aspect</label>
                <select
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
                  value={b.payload.aspect || '16:9'}
                  onChange={(e) => update({ aspect: e.target.value })}
                >
                  <option value="1:1">1:1</option>
                  <option value="4:3">4:3</option>
                  <option value="16:9">16:9</option>
                  <option value="full">Full width</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Fit</label>
                <select
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
                  value={b.payload.fit || 'cover'}
                  onChange={(e) => update({ fit: e.target.value })}
                >
                  <option value="cover">Cover</option>
                  <option value="contain">Contain</option>
                </select>
              </div>
            </div>
          </div>
        );
      case 'rive':
        return (
          <div className="space-y-3">
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Rive asset URL</label>
            <input
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
              value={b.payload.asset_url || ''}
              onChange={(e) => update({ asset_url: e.target.value })}
              placeholder="/static/lesson_rive/..."
            />
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <input
                  type="file"
                  accept=".riv,application/vnd.rive"
                  className="hidden"
                  id="rive-upload-input"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (!LESSON_API) {
                      alert('Set VITE_LESSON_API_URL to upload Rive files.');
                      return;
                    }
                    if (file.size > 10 * 1024 * 1024) {
                      alert('Rive file too large (max 10MB).');
                      return;
                    }
                    setRiveUploading(true);
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const token = localStorage.getItem('admin_token');
                      const res = await fetch(`${LESSON_API}/lessons/assets/rive`, {
                        method: 'POST',
                        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                        body: formData,
                      });
                      if (!res.ok) {
                        const text = await res.text();
                        throw new Error(text || 'Upload failed');
                      }
                      const data = await res.json().catch(() => ({}));
                      const url = data.asset_url || data.url;
                      if (url) {
                        update({ asset_url: url });
                      }
                    } catch (err: any) {
                      alert(err?.message || 'Upload failed');
                    } finally {
                      setRiveUploading(false);
                      e.target.value = '';
                    }
                  }}
                />
                <span className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-700 cursor-pointer bg-gray-50 dark:bg-gray-800">
                  <label htmlFor="rive-upload-input" className="cursor-pointer">
                    {riveUploading ? 'Uploading…' : 'Upload .riv'}
                  </label>
                </span>
              </label>
              <span className="text-[11px] text-gray-500 dark:text-gray-400">
                Max 10MB • .riv
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Artboard</label>
                <input
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
                  value={b.payload.artboard || ''}
                  onChange={(e) => update({ artboard: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">State machine</label>
                <input
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
                  value={b.payload.stateMachine || ''}
                  onChange={(e) => update({ stateMachine: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={b.payload.autoplay ?? true}
                  onChange={(e) => update({ autoplay: e.target.checked })}
                />
                Autoplay
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={b.payload.loop !== false}
                  onChange={(e) => update({ loop: e.target.checked ? 'auto' : false })}
                />
                Loop
              </label>
            </div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Speed</label>
            <input
              type="number"
              step="0.1"
              min="0.1"
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
              value={b.payload.speed ?? 1}
              onChange={(e) => update({ speed: parseFloat(e.target.value) || 1 })}
            />
          </div>
        );
      case 'mcq': {
        const align = b.payload.question_align || 'left';
        const htmlVal = b.payload.question_html || '';
        const toPlain = (html: string) => {
          const tmp = document.createElement('div');
          tmp.innerHTML = html || '';
          return tmp.textContent || tmp.innerText || '';
        };
        return (
          <div className="space-y-3">
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Question</label>
            <RichTextEditor
              value={htmlVal}
              onChange={(html) => update({ question_html: html, question: toPlain(html) })}
              placeholder="Type question..."
              align={align}
              onAlignChange={(a) => update({ question_align: a })}
            />
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">Options</div>
                <button
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                  onClick={() => {
                    const options = b.payload.options || [];
                    const next = String.fromCharCode(65 + options.length);
                    update({
                      options: [...options, { id: next, text: `Option ${next}`, is_correct: false }],
                    });
                  }}
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              {(b.payload.options || []).map((opt: any, idx: number) => (
                <div
                  key={opt.id}
                  className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2"
                >
                  <button
                    className={`p-1 rounded ${opt.is_correct ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'}`}
                    onClick={() => {
                      const options = (b.payload.options || []).map((o: any, i: number) => ({
                        ...o,
                        is_correct: i === idx,
                      }));
                      update({ options });
                    }}
                    title="Mark correct"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <input
                    className="flex-1 bg-transparent text-sm"
                    value={opt.text}
                    onChange={(e) => {
                      const options = [...(b.payload.options || [])];
                      options[idx] = { ...opt, text: e.target.value };
                      update({ options });
                    }}
                  />
                  <button
                    className="p-1 text-red-500 hover:text-red-700"
                    onClick={() => {
                      const options = [...(b.payload.options || [])];
                      options.splice(idx, 1);
                      update({ options });
                    }}
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Explanation</label>
            <textarea
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
              rows={2}
              value={b.payload.explanation || ''}
              onChange={(e) => update({ explanation: e.target.value })}
            />
          </div>
        );
      }
      case 'true_false':
        return (
          <div className="space-y-3">
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Statement</label>
            <textarea
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
              rows={3}
              value={b.payload.statement || ''}
              onChange={(e) => update({ statement: e.target.value })}
            />
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="radio"
                  name={`tf-${b.id}`}
                  checked={b.payload.correct === true}
                  onChange={() => update({ correct: true })}
                />
                True
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="radio"
                  name={`tf-${b.id}`}
                  checked={b.payload.correct === false}
                  onChange={() => update({ correct: false })}
                />
                False
              </label>
            </div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Explanation</label>
            <textarea
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
              rows={2}
              value={b.payload.explanation || ''}
              onChange={(e) => update({ explanation: e.target.value })}
            />
          </div>
        );
      case 'flip_card_grid':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Columns</label>
                <input
                  type="number"
                  min={1}
                  max={3}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
                  value={b.payload.columns || 2}
                  onChange={(e) => update({ columns: Number(e.target.value) || 2 })}
                />
              </div>
              <div className="flex items-end justify-end">
                <button
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                  onClick={() => {
                    const cards = b.payload.cards || [];
                    const id = `card-${Date.now()}`;
                    update({
                      cards: [...cards, { id, front: 'Front', back: 'Back', image: '' }],
                    });
                  }}
                >
                  <Plus className="w-3 h-3" /> Add card
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {(b.payload.cards || []).map((card: any, idx: number) => (
                <div key={card.id || idx} className="border border-gray-200 dark:border-gray-800 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-gray-600 dark:text-gray-300">Card {idx + 1}</div>
                    <button
                      className="p-1 text-red-500 hover:text-red-700"
                      onClick={() => {
                        const cards = [...(b.payload.cards || [])];
                        cards.splice(idx, 1);
                        update({ cards });
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
                    value={card.front || ''}
                    onChange={(e) => {
                      const cards = [...(b.payload.cards || [])];
                      cards[idx] = { ...card, front: e.target.value };
                      update({ cards });
                    }}
                    placeholder="Front"
                  />
                  <input
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
                    value={card.back || ''}
                    onChange={(e) => {
                      const cards = [...(b.payload.cards || [])];
                      cards[idx] = { ...card, back: e.target.value };
                      update({ cards });
                    }}
                    placeholder="Back"
                  />
                </div>
              ))}
            </div>
          </div>
        );
      case 'link_preview':
        return (
          <div className="space-y-3">
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">URL</label>
            <input
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
              value={b.payload.url || ''}
              onChange={(e) => update({ url: e.target.value })}
              placeholder="https://..."
            />
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Title</label>
            <input
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
              value={b.payload.title || ''}
              onChange={(e) => update({ title: e.target.value })}
            />
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Description</label>
            <textarea
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
              rows={2}
              value={b.payload.description || ''}
              onChange={(e) => update({ description: e.target.value })}
            />
          </div>
        );
      case 'spacer': {
        const height = Number(b.payload.height ?? 24);
        return (
          <div className="space-y-3">
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">
              Spacer height (px)
            </label>
            <input
              type="range"
              min={4}
              max={320}
              value={height}
              onChange={(e) => update({ height: Number(e.target.value) })}
              className="w-full"
            />
            <input
              type="number"
              min={4}
              max={320}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
              value={height}
              onChange={(e) => {
                const val = Math.min(320, Math.max(4, Number(e.target.value) || 4));
                update({ height: val });
              }}
            />
            <div className="text-xs text-gray-500 dark:text-gray-400">Adds vertical gap between blocks.</div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="w-80 border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-2xl shadow-sm p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-50">Editing</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">{block.type.replace('_', ' ')}</div>
        </div>
        <button
          className="text-xs text-red-500 hover:text-red-600"
          onClick={() => deleteBlock(page.id, block.id)}
        >
          Remove
        </button>
      </div>

      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Label</label>
      <input
        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
        value={block.title || ''}
        onChange={(e) => updateBlock(page.id, block.id, { title: e.target.value })}
        placeholder="Optional label for dashboard"
      />

      {renderEditor(block)}
    </div>
  );
};
